"use client"

import * as React from "react"
import {
    ColumnDef,
    ColumnFiltersState,
    SortingState,
    VisibilityState,
    flexRender,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    useReactTable,
} from "@tanstack/react-table"
import { ArrowUpDown, ChevronDown, MoreHorizontal } from "lucide-react"
import { Wallet } from "lucide-react"; // ou icône de ton choix
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import {
    DropdownMenu,
    DropdownMenuCheckboxItem,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Input } from "@/components/ui/input"
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table"
import { ReloadModel } from "@/constants"
import { useNavigate } from "react-router-dom"
import { useContext, useEffect, useState } from "react"
import { Spinner } from "@/components/Spinner"
import { depositService } from "@/services/depositService"
import { AuthContext } from "@/contexts/authContext"



const columns: ColumnDef<ReloadModel>[] = [
    {
        id: "select",
        header: ({ table }) => (
            <Checkbox
                checked={
                    table.getIsAllPageRowsSelected() ||
                    (table.getIsSomePageRowsSelected() && "indeterminate")
                }
                onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
                aria-label="Tout sélectionner"
            />
        ),
        cell: ({ row }) => (
            <Checkbox
                checked={row.getIsSelected()}
                onCheckedChange={(value) => row.toggleSelected(!!value)}
                aria-label="Select row"
            />
        ),
        enableSorting: false,
        enableHiding: false,
    },
    {
        accessorKey: "date_created",
        header: ({ column }) => {
            return (
                <Button
                    variant="ghost"
                    onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
                >
                    Date
                    <ArrowUpDown />
                </Button>
            )
        },
        cell: ({ row }) => <div className="">{row.getValue("date_created")}</div>,
    },
    {
        accessorKey: "nom",
        header: "Nom du joueur",
        cell: ({ row }) => (
            <div className="">{row.getValue("nom")}</div>
        ),
    },
    {
        accessorKey: "montant",
        header: () => <div className="text-right">Montant</div>,
        cell: ({ row }) => {
            const amount = parseFloat(row.getValue("montant"))

            const formatted = new Intl.NumberFormat("fr-FR", {
                style: "currency",
                currency: "XOF",
            }).format(amount)

            return <div className="text-right font-medium">{formatted}</div>
        },
    },
    {
        accessorKey: "numero_commercial",
        header: "Numéro de l'agent",
        cell: ({ row }) => (
            <div className="">{row.getValue("numero_commercial")}</div>
        ),
    },

    {
        id: "actions",
        enableHiding: false,
        cell: ({ row }) => {
            const payment = row.original

            return (
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button variant="ghost" className="h-8 w-8 p-0">
                            <span className="sr-only">Open menu</span>
                            <MoreHorizontal />
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                        <DropdownMenuLabel>Actions</DropdownMenuLabel>
                        <DropdownMenuItem
                            onClick={() => navigator.clipboard.writeText(payment.id)}
                        >
                            Copier l'id de la recharge
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem>Voir les détails de la recharge</DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
            )
        },
    },
]

export function ReloadHistoryDataTable() {
    const [sorting, setSorting] = React.useState<SortingState>([])
    const [isloading, setIsLoading] = useState<boolean>(false);
    const [depotList, setDepotList] = useState<ReloadModel[]>([]);

    const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
        []
    )
    const { user } = useContext(AuthContext);

    const [columnVisibility, setColumnVisibility] =
        React.useState<VisibilityState>({})
    const [rowSelection, setRowSelection] = React.useState({})
    const navigate = useNavigate();

    const table = useReactTable({
        data: depotList,
        columns,
        onSortingChange: setSorting,
        onColumnFiltersChange: setColumnFilters,
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        onColumnVisibilityChange: setColumnVisibility,
        onRowSelectionChange: setRowSelection,

        state: {
            sorting,
            columnFilters,
            columnVisibility,
            rowSelection,
        },
        initialState: {
            pagination: {
                pageSize: 50,
            },
        },
    })
    useEffect(() => {
        setIsLoading(true);
        const fetchDeposits = async () => {
            try {
                if (user?.phone_number != null) {
                    setIsLoading(true);

                    const result = await depositService.fetchDepositHistory(user?.phone_number);
                    if (result?.depots) {
                        setDepotList(result?.depots);
                    }
                    setIsLoading(false);

                }
            } catch (error) {
                console.error("Erreur lors de la récupération des dépôts :", error);
            }
        };

        fetchDeposits();
    }, [user]);





    if (isloading) {
        return <Spinner />;

    }

    else {
        return (
            <div className="w-full">
                <div className="flex flex-col md:flex-row items-center py-4 space-y-4 md:space-y-0 md:space-x-4">
                    <Input
                        placeholder="Filtrer par Nom du joueur..."
                        value={(table.getColumn("nom")?.getFilterValue() as string) ?? ""}
                        onChange={(event) =>
                            table.getColumn("nom")?.setFilterValue(event.target.value)
                        }
                        className="w-full mr-auto"
                    />
                    <a href="/affiliate-withdraw">
                    <Button variant="default" className="w-full md:w-auto ml-0 md:ml-3 mr-0 md:mr-3">
                        Demander un retrait
                    </Button>
                    </a>
                    <DropdownMenu >
                        <DropdownMenuTrigger asChild>
                            <Button variant="outline" className="w-full md:w-auto ml-auto">
                                Colonnes <ChevronDown />
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                            {table
                                .getAllColumns()
                                .filter((column) => column.getCanHide())
                                .map((column) => {
                                    return (
                                        <DropdownMenuCheckboxItem
                                            key={column.id}
                                            className="capitalize"
                                            checked={column.getIsVisible()}
                                            onCheckedChange={(value) =>
                                                column.toggleVisibility(!!value)
                                            }
                                        >
                                            {column.id}
                                        </DropdownMenuCheckboxItem>
                                    )
                                })}
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>
                <div className="w-full bg-blue-500 text-white text-center py-2 rounded-sm mb-5">
                    Copier et envoyez ce lien à vos amis pour gagner de l'argent à chaque recharge via votre intermediare : <br />
                    <span
                        className="font-bold cursor-pointer text-sm"
                        onClick={() =>{
                            navigator.clipboard.writeText(`https://easyservicebf.web.app/share/${user?.phone_number}`);
                            alert("Lien copié dans le presse-papier");
                        }}
                    >
                        <a className="text-sm" href={`https://easyservicebf.web.app/share/${user?.phone_number}`} target="_blank" rel="noopener noreferrer">
                        https://easyservicebf.web.app/share/{user?.phone_number}
                        </a>
                    </span>
                </div>

                <h2 className="sr-only mb-4 text-black">Liste des dernieres recharges</h2>
                <div className="rounded-md border">

                    <Table>
                        <TableHeader>
                            {table.getHeaderGroups().map((headerGroup) => (
                                <TableRow key={headerGroup.id}>
                                    {headerGroup.headers.map((header) => {
                                        return (
                                            <TableHead key={header.id}>
                                                {header.isPlaceholder
                                                    ? null
                                                    : flexRender(
                                                        header.column.columnDef.header,
                                                        header.getContext()
                                                    )}
                                            </TableHead>
                                        )
                                    })}
                                </TableRow>
                            ))}
                        </TableHeader>
                        <TableBody>
                            {table.getRowModel().rows?.length ? (
                                table.getRowModel().rows.map((row) => (
                                    <TableRow
                                        key={row.id}
                                        data-state={row.getIsSelected() && "selected"}
                                    >
                                        {row.getVisibleCells().map((cell) => (
                                            <TableCell key={cell.id}>
                                                {flexRender(
                                                    cell.column.columnDef.cell,
                                                    cell.getContext()
                                                )}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell
                                        colSpan={columns.length}
                                        className="h-24 text-center"
                                    >
                                        pas de résultats.
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </div>
                <div className="flex items-center justify-end space-x-2 py-4">
                    <div className="flex-1 text-sm text-muted-foreground">
                        {table.getFilteredSelectedRowModel().rows.length} of{" "}
                        {table.getFilteredRowModel().rows.length} ligne(s) sélectionnées.
                    </div>
                    <div className="space-x-2">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => table.previousPage()}
                            disabled={!table.getCanPreviousPage()}
                        >
                            Précedent
                        </Button>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => table.nextPage()}
                            disabled={!table.getCanNextPage()}
                        >
                            Suivant
                        </Button>
                    </div>
                </div>
            </div>
        )
    }
}
